/**
 * Lifts step — register at least one lift (#1853, #1498).
 *
 * Scheduling assigns work to lifts, so with none the calendar is an empty
 * frame.
 *
 * `LiftFormDialog` takes `{ open, lift, onClose }` and has no `onSuccess` —
 * cancelling and succeeding both call `onClose`, so the dialog cannot tell this
 * step whether a lift was actually created. Completion is therefore read from
 * the lifts query instead: a fact, not an event. That is also correct on a
 * revisit, where lifts may already exist from an earlier session.
 */

import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { useLifts } from '@/hooks/useLifts';
import { LiftFormDialog } from '@/features/settings/equipment/LiftFormDialog';

interface LiftsStepProps {
  canManage: boolean;
  /** Reported upward so the wizard can mark the step done without duplicating the query. */
  onLiftCountChange: (count: number) => void;
}

export function LiftsStep({ canManage, onLiftCountChange }: LiftsStepProps) {
  const { t } = useTranslation('onboarding');
  const [dialogOpen, setDialogOpen] = useState(false);
  const { data, isLoading } = useLifts();

  const liftCount = data?.pages.reduce((sum, page) => sum + page.data.length, 0) ?? 0;
  const hasLift = liftCount > 0;

  // Report the count as it changes. `useEffect` rather than a render-time call
  // because this sets state in the parent.
  const lastReported = useRef<number | null>(null);
  useEffect(() => {
    if (isLoading || lastReported.current === liftCount) return;
    lastReported.current = liftCount;
    onLiftCountChange(liftCount);
  }, [liftCount, isLoading, onLiftCountChange]);

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('lifts.intro')}
      </Typography>

      <Stack spacing={2} alignItems="flex-start">
        {canManage ? (
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
            {t('lifts.add')}
          </Button>
        ) : (
          <Alert severity="info" sx={{ width: '100%' }}>
            {t('lifts.noPermission')}
          </Alert>
        )}

        {isLoading ? (
          <CircularProgress size={20} />
        ) : hasLift ? (
          <Alert severity="success" sx={{ width: '100%' }}>
            {t('lifts.registered', { count: liftCount })}
          </Alert>
        ) : (
          <Alert severity="info" sx={{ width: '100%' }}>
            {t('lifts.none')}
          </Alert>
        )}
      </Stack>

      {dialogOpen && <LiftFormDialog open onClose={() => setDialogOpen(false)} />}
    </Box>
  );
}
